/**
 * Centralized Permission Configuration
 *
 * This file defines all role-based access control (RBAC) permissions for the application.
 * Permissions are organized by role and specify which pages/actions each role can access.
 *
 * @module PermissionConfig
 */

/**
 * Available user roles in the system
 */
export enum UserRole {
  MANAGER = 'manager',
  DATA_VIEWER = 'data_viewer',
  DATA_ANALYST = 'data_analyst',
  DATA_COLLECTOR = 'data_collector',
  DATA_DOWNLOADER = 'data_downloader',
  FORM_DESIGNER = 'form_designer',
  QC_EDITOR = 'qc_editor',
  // Virtual role: any authenticated user who belongs to an organization
  ORGANIZATION_MEMBER = 'organization_member',
}

/**
 * Route names that require permissions
 * This enum provides type-safe route name references
 */
export enum ProtectedRoute {
  // Dashboard
  DASHBOARD = 'dashboard',

  // Settings & Preferences
  SETTINGS = 'settings',
  PREFERENCES = 'preferences',
  FAQ = 'faq',

  // Projects
  PROJECTS = 'projects',
  PROJECT_DETAIL = 'project-detail',
  PROJECT_FORMS = 'project-forms',
  PROJECT_USERS = 'project-users',
  PROJECT_QR_CODE = 'project-qr-code',
  PROJECT_FORM_ACCESS = 'project-form-access',
  PROJECT_CLEANUP = 'project-cleanup',
  PROJECT_INFO = 'project-info',
  PROJECT_MAP = 'project-map',
  PROJECT_MAP_SETTINGS = 'project-map-settings',

  // Forms
  FORM_DETAIL = 'form-detail',
  FORM_DASHBOARD = 'form-dashboard',
  FORM_AUTO_REPORTS = 'form-auto-reports',
  FORM_QC = 'form-qc',
  FORM_QC_CREATE_RULE = 'form-qc-create-rule',
  FORM_QC_EDIT_RULE = 'form-qc-edit-rule',
  FORM_DATA_EXPORT = 'form-data-export',
  FORM_DATA_ANALYSIS = 'form-data-analysis',
  FORM_DATA_MONITORING = 'form-data-monitoring',
  FORM_SETTINGS = 'form-settings',
  FORM_DATASETS = 'form-datasets',

  // ODK
  ODK_SERVERS = 'odk-servers',
  ODK_SYNC = 'odk-sync',

  // Organization
  MY_ORGANIZATION = 'my-organization',
  USER_MANAGEMENT = 'user-management',
  ORGANIZATION_STATS = 'organization-stats',
  ORGANIZATION_SETTINGS = 'organization-settings',
  ORGANIZATION_REPORT_TEMPLATES = 'organization-report-templates',
  ORGANIZATION_ODK_IMPORT_PROJECTS = 'organization-odk-import-projects',
  ORGANIZATION_ODK_IMPORT_FORMS = 'organization-odk-import-forms',
  XLSFORM_ASSIST = 'xlsform-assist',
}

/**
 * Permission requirement types
 * - ANY: User needs at least one of the specified roles
 * - ALL: User needs all of the specified roles
 */
export type PermissionRequirement = {
  type: 'ANY' | 'ALL'
  roles: UserRole[]
}

/**
 * Centralized route permission mappings
 *
 * Structure:
 * - Key: Route name (from ProtectedRoute enum)
 * - Value: Permission requirement specifying which roles can access the route
 *
 * Permission Inheritance:
 * - Child routes inherit parent route permissions by default
 * - Child routes can specify additional/stricter permissions
 * - The permission guard will check BOTH parent and child permissions
 *
 * Example:
 * If parent requires ['manager', 'data_viewer'] (ANY)
 * And child requires ['manager'] (ALL)
 * Then user must have 'manager' role to access the child
 */
export const ROUTE_PERMISSIONS: Record<string, PermissionRequirement> = {
  // Dashboard - accessible to most roles
  [ProtectedRoute.DASHBOARD]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.DATA_ANALYST, UserRole.FORM_DESIGNER],
  },

  // Settings - manager only
  [ProtectedRoute.SETTINGS]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  // Preferences - accessible to key roles
  [ProtectedRoute.PREFERENCES]: {
    type: 'ANY',
    // Allow basic access to any organization member even without explicit project roles
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.FORM_DESIGNER, UserRole.ORGANIZATION_MEMBER],
  },

  // FAQ - accessible to all authenticated users
  [ProtectedRoute.FAQ]: {
    type: 'ANY',
    roles: [
      UserRole.MANAGER,
      UserRole.DATA_VIEWER,
      UserRole.DATA_ANALYST,
      UserRole.DATA_COLLECTOR,
      UserRole.FORM_DESIGNER,
    ],
  },

  // Projects - accessible to key roles
  [ProtectedRoute.PROJECTS]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.DATA_ANALYST, UserRole.FORM_DESIGNER],
  },

  // Project Detail - parent route
  [ProtectedRoute.PROJECT_DETAIL]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.DATA_ANALYST, UserRole.FORM_DESIGNER],
  },

  // Project sub-pages
  [ProtectedRoute.PROJECT_FORMS]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.DATA_ANALYST, UserRole.FORM_DESIGNER],
  },

  [ProtectedRoute.PROJECT_USERS]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  [ProtectedRoute.PROJECT_QR_CODE]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.FORM_DESIGNER],
  },

  [ProtectedRoute.PROJECT_FORM_ACCESS]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.FORM_DESIGNER],
  },

  [ProtectedRoute.PROJECT_CLEANUP]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  [ProtectedRoute.PROJECT_INFO]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.DATA_ANALYST, UserRole.FORM_DESIGNER],
  },

  [ProtectedRoute.PROJECT_MAP]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.DATA_ANALYST, UserRole.FORM_DESIGNER],
  },

  [ProtectedRoute.PROJECT_MAP_SETTINGS]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  // ODK Servers - manager only
  [ProtectedRoute.ODK_SERVERS]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  // ODK Sync
  [ProtectedRoute.ODK_SYNC]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  // Organization - manager only
  [ProtectedRoute.MY_ORGANIZATION]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  [ProtectedRoute.USER_MANAGEMENT]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  [ProtectedRoute.ORGANIZATION_STATS]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  [ProtectedRoute.ORGANIZATION_SETTINGS]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  [ProtectedRoute.ORGANIZATION_REPORT_TEMPLATES]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  [ProtectedRoute.ORGANIZATION_ODK_IMPORT_PROJECTS]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  [ProtectedRoute.ORGANIZATION_ODK_IMPORT_FORMS]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  [ProtectedRoute.XLSFORM_ASSIST]: {
    type: 'ALL',
    roles: [UserRole.MANAGER],
  },

  // Form Detail - parent route
  [ProtectedRoute.FORM_DETAIL]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.DATA_ANALYST, UserRole.FORM_DESIGNER],
  },

  // Form sub-pages
  [ProtectedRoute.FORM_DASHBOARD]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.DATA_ANALYST, UserRole.FORM_DESIGNER],
  },

  [ProtectedRoute.FORM_AUTO_REPORTS]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.DATA_ANALYST, UserRole.FORM_DESIGNER],
  },

  [ProtectedRoute.FORM_QC]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.QC_EDITOR],
  },

  [ProtectedRoute.FORM_QC_CREATE_RULE]: {
    type: 'ALL',
    roles: [UserRole.QC_EDITOR],
  },

  [ProtectedRoute.FORM_QC_EDIT_RULE]: {
    type: 'ALL',
    roles: [UserRole.QC_EDITOR],
  },

  [ProtectedRoute.FORM_DATA_EXPORT]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_DOWNLOADER, UserRole.DATA_ANALYST],
  },

  [ProtectedRoute.FORM_DATA_ANALYSIS]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_ANALYST],
  },

  [ProtectedRoute.FORM_DATA_MONITORING]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER],
  },

  [ProtectedRoute.FORM_SETTINGS]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.FORM_DESIGNER],
  },

  [ProtectedRoute.FORM_DATASETS]: {
    type: 'ANY',
    roles: [UserRole.MANAGER, UserRole.DATA_VIEWER, UserRole.DATA_ANALYST, UserRole.FORM_DESIGNER],
  },
}

/**
 * Default fallback behavior for routes without explicit permission configuration
 *
 * Strategy: DENY_BY_DEFAULT
 * - If a route requires authentication but has no permission configuration, access is denied
 * - This ensures security by default and forces explicit permission declarations
 * - Unconfigured access attempts are logged for monitoring
 */
export const DEFAULT_PERMISSION_BEHAVIOR = 'DENY_BY_DEFAULT' as const

/**
 * Unauthorized access handling configuration
 */
export const UNAUTHORIZED_ACCESS_CONFIG = {
  /**
   * Route to redirect to when access is denied
   */
  redirectRoute: 'not-found-page',

  /**
   * Whether to log unauthorized access attempts
   */
  logAttempts: true,

  /**
   * Whether to show a notification to the user
   */
  showNotification: true,

  /**
   * Notification message for unauthorized access
   */
  notificationMessage: 'You do not have permission to access this page',
}

/**
 * Helper function to get permission requirements for a route
 *
 * @param routeName - The name of the route
 * @returns Permission requirement or null if not configured
 */
export function getRoutePermissions(routeName: string): PermissionRequirement | null {
  return ROUTE_PERMISSIONS[routeName] || null
}

/**
 * Helper function to check if a route has explicit permissions configured
 *
 * @param routeName - The name of the route
 * @returns True if route has explicit permissions, false otherwise
 */
export function hasExplicitPermissions(routeName: string): boolean {
  return routeName in ROUTE_PERMISSIONS
}

/**
 * Helper function to get all roles that can access a specific route
 *
 * @param routeName - The name of the route
 * @returns Array of roles that can access the route
 */
export function getAllowedRoles(routeName: string): UserRole[] {
  const permissions = getRoutePermissions(routeName)
  return permissions ? permissions.roles : []
}
